import { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { Bar, BarChart, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { StatusBreakdownItem } from '@/apis/agency/onboardingInsights/types';
import { STATUS_CONFIG, STATUS_ORDER } from '../../constants';

interface Props {
  data: StatusBreakdownItem[];
}

export default function StatusBreakdownChart({ data }: Props) {
  const chartData = useMemo(() => {
    const countByStatus = data.reduce<Record<string, number>>((acc, item) => {
      acc[item.status] = item.count;
      return acc;
    }, {});

    return STATUS_ORDER.map((status) => ({
      name: STATUS_CONFIG[status].label,
      value: countByStatus[status] ?? 0,
      color: STATUS_CONFIG[status].chartColor
    }));
  }, [data]);

  return (
    <Box bgcolor='#fff' border='1px solid #ECECEC' borderRadius={2} p={2} height='100%'>
      <Typography fontSize={18} fontWeight={500}>
        Onboarding Status Breakdown
      </Typography>
      <Typography variant='body2' color='#777' mt={0.5} mb={2}>
        Overview of current status across all visible records
      </Typography>

      <ResponsiveContainer width='100%' height={420}>
        <BarChart data={chartData} layout='vertical' margin={{ left: 10, right: 30, top: 0, bottom: 0 }}>
          <XAxis type='number' allowDecimals={false} />
          <YAxis
            type='category'
            dataKey='name'
            width={180}
            interval={0}
            tick={({ x, y, payload }) => (
              <text x={x} y={y} dy={4} textAnchor='end' fontSize={12} fill='#666'>
                {payload.value}
              </text>
            )}
          />
          <Tooltip />
          <Bar dataKey='value' radius={[0, 8, 8, 0]} barSize={29}>
            {chartData.map((entry, index) => (
              <Cell key={index} fill={entry.color} />
            ))}
            <LabelList dataKey='value' position='right' style={{ fill: '#6b7280', fontSize: 12 }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}
